<template>
  <div class="left">
    <StepperCard
      path="/cart"
      title="Choose Your Dumpster"
      :isFutureStep="false"
      :isDumpsterStepper="true"
    />
    <StepperCard
      path="/cart/service"
      title="Service Info &amp; Placement"
      :isFutureStep="false"
    />
    <h2 class="header">
      <span class="header__number">3</span>Payment Information
    </h2>
    <p class="disclaimer disclaimer__italic">* indicates a required field</p>
    <form id="paymentForm" class="payment-form" @submit.prevent="submitPaymentEvents">
      <div class="ecom checkbox">
        <label class="ecom checkbox__label">
          <div v-if="!billingSameAsDelivery" class="ecom checkbox__fauxbox"></div>
          <img class="ecom checkbox__check-img" v-if="billingSameAsDelivery" :src="activeCheckbox">
          <input
            v-model="billingSameAsDelivery"
            @change="updateBillingFields"
            class="ecom checkbox__input"
            type="checkbox"
          />
          <span class="ecom checkbox__text">
            Check if Billing Information is the same as Service &amp; Contact Info
          </span>
        </label>
      </div>
      <fieldset v-show="!billingSameAsDelivery" class="fieldset mt-2" ref="billingFields">
        <legend class="legend">Billing Information</legend>
        <label class="label">
          First Name *
          <input
            v-model="billingFirstName"
            :required="!billingSameAsDelivery"
            class="input"
            placeholder="Enter Billing First Name*"
            type="text"
          />
        </label>
        <label class="label">
          Last Name *
          <input
            v-model="billingLastName"
            :required="!billingSameAsDelivery"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('Last name required. Digits not allowed.')"
            pattern="[^0-9]+"
            placeholder="Enter Billing Last Name*"
            type="text"
          />
        </label>
        <label class="label">
          Email *
          <input
            v-model="billingEmailAddress"
            :required="!billingSameAsDelivery"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('This is not a valid email address.')"
            pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}"
            placeholder="Enter Billing Email *"
            type="email"
          />
        </label>
        <label class="label">
          Phone Number *
          <small class="payment-form__helper">Provide the number of the best person to contact for questions regarding this order</small>
          <input
            v-model="billingPhone"
            v-maska="phoneMask"
            :required="!billingSameAsDelivery"
            class="input"
            onchange="this.setCustomValidity('')"
            oninvalid="this.setCustomValidity('Please enter a valid phone number.')"
            placeholder="(999) 999-9999 *"
            ref="billingPhone"
            type="tel"
          />
        </label>
        <label class="label">
          Billing Address *
          <input
            v-model="billingAddress"
            @blur="addressBlur"
            @input="addressInput"
            :class="['input', { 'input__with-error': this.showAddressError }]"
            :required="!billingSameAsDelivery"
            autocomplete="off"
            id="autocomplete"
            name="search"
            placeholder="Enter Billing Address*"
            type="text"
          />
        </label>
        <dismissable :isWarning="true" :showClose="false" v-if="showAddressError">
          <template v-slot:content>
            <p>Please choose a valid address from the drop-down. Don't see your address?
              Reach out by phone at <a :href="`tel:${phoneNumber}`">{{ phoneNumber }}</a> or LiveChat.</p>
          </template>
        </dismissable>
        <label class="label">
          Apt, Suite, etc. (optional)
          <input
            v-model="billingApartmentNumber"
            class="input"
            placeholder="Apt, Suite, etc. (optional)"
            type="text"
          />
        </label>
      </fieldset>
      <h2 class="legend mt-2">Customer Service Agreement</h2>
      <div class="infobox">
        <p class="mb-1">By reading and accepting {{companyNameCSA}} Customer Service Agreement, you are confirming that you understand and agree to comply with the following delivery, pricing, scheduling and dumpster weight guidelines.</p>
        <p class="mb-1">We will not automatically come to pick up the dumpster rental at any point. You must schedule your dumpster pickup by no later than the last date of your rental period, which is {{endOfRentalPeriodDate}}. You can schedule your dumpster pickup by visiting the {{companyName}} website and <a target="_blank" rel="noopener" :href="scheduleAPickup">filling out this form</a>. If you keep the dumpster past the allotted rental period and have not scheduled a pickup by the end of this rental period, you will be charged an extension fee of ${{rentalPeriodOverage}} for each additional day.</p>
        <p class="mb-1">This price includes the first {{tonnage}} ton(s) of your disposal. If you exceed this weight limit, you will be subject to a ${{tonnageOverage}} per-ton prorated fee which will be assessed at the landfill. Our trucks cannot haul more than 10 tons total.</p>
        <p class="mb-1">Please do not use the phone number printed on your dumpster for any service questions or pickup requests. Instead, contact {{companyName}} directly at <a :href="`tel:+${phoneNumber}`">{{phoneNumber}}</a> for any related queries or needs.</p>
        <p class="mb-1">When you are filling your dumpster, please load it as evenly as you can to prevent overloading. This will also help you maximize the amount of space inside for your items. Additionally, keep the debris inside the container level and make sure that no items are sticking above the maximum load line. Overloading the container may result in a $195 trip fee if our driver cannot pick up your dumpster.</p>
        <p class="mb-1">There are a few items that we cannot accept in the dumpster. These include tires, batteries and railroad ties. We also cannot accept anything flammable, toxic or hazardous. Finally, we cannot accept any paints, oils, asbestos, refrigerators, air conditioners, food waste or 50-gallon drums. For more detailed information about <a target="_blank" rel="noopener" :href="prohibitedItems">prohibited items</a>, please review your invoice or contact us with questions about your debris types.</p>
        <p class="mb-1"><strong>If you dispose of a restricted or prohibited item in your dumpster, you may need to pay an additional disposal fee. You can avoid these fees by following your area disposal regulations and the guidelines on your invoice.</strong></p>
        <p class="mb-1">Our delivery window runs from 6 a.m. – 6 p.m. on the day of delivery. If we cannot deliver the container for any reason outside of the driver’s control, there will be a $195 trip fee. Lastly, if you need to cancel the delivery, you have until 3 p.m. the day before for a $25 cancellation fee. If you cancel or make changes after 3 p.m. the day before the delivery, or on the day of delivery, there will be a $195 fee.</p>
        <p>Terms and conditions are subject to change, and the final details of your purchase will be emailed to you with your order confirmation.</p>
      </div>
      <div class="checkbox mt-2">
        <label class="checkbox__label checkbox__label-top-align">
          <div v-if="!billingCustomerServiceAgreement" class="checkbox__fauxbox"></div>
          <img class="checkbox__check-img" v-if="billingCustomerServiceAgreement" :src="activeCheckbox">
          <input
            @change="csaToggle"
            v-model="billingCustomerServiceAgreement"
            class="checkbox__input"
            type="checkbox"
            required
          />
          <span class="checkbox__text">
            <strong>I have read and agree to the Customer Service Agreement.</strong> In doing so I acknowledge and accept the following:
            <ul class="checkbox__list">
              <li>Exceeding my rental period will result in an additional charge of <strong>${{rentalPeriodOverage}}</strong> per day.</li>
              <li>Exceeding the allotted debris tonnage included in your Flat Rate Price will result in a prorated charge of up to <strong>${{tonnageOverage}}</strong> per additional ton.</li>
              <li>Throwing away restricted items in my dumpster may result in an additional charge.</li>
              <li>I have read and accept the <a :href="serviceAgreementLink" target="_blank">Service Agreement Terms &amp; Conditions</a></li>
            </ul>
          </span>
        </label>
      </div>
      <fieldset class="fieldset mt-2">
        <legend class="legend">Service Notifications</legend>
        <p>Enter your cell phone number and subscribe to SMS updates about your service.</p>
        <label class="label">
          <input
            v-model="billingMobilePhoneNumber"
            v-maska="phoneMask"
            class="input"
            placeholder="Mobile Phone Number (optional)"
            ref="servicePhoneNumber"
            type="tel"
          />
        </label>
      </fieldset>
      <div class="checkbox">
        <label class="checkbox__label">
          <div v-if="!consentToReceiveTexts" class="checkbox__fauxbox"></div>
          <img class="checkbox__check-img" v-if="consentToReceiveTexts" :src="activeCheckbox">
          <input
            v-model="consentToReceiveTexts"
            class="checkbox__input"
            type="checkbox"
          />
          <span class="checkbox__text">
            I consent to receive informational texts from and on behalf of {{companyName}} to the phone number I provided above. Message &amp; Data Rates may apply.
          </span>
        </label>
      </div>
      <fieldset class="fieldset mt-2">
        <legend class="legend">Add a Promotional Code</legend>
        <label class="label">
          <input
            @keydown.enter.prevent="getDiscount"
            v-model="discountCode"
            class="input"
            placeholder="Enter Code"
            type="text"
          />
        </label>
      </fieldset>
      <div class="input__accessories">
        <div class="input__message" v-if="discountCodeValid != null">
          <img class="input__status-icon" :src="discountCodeValid ? successIcon : declinedIcon"/>
          <span v-if="discountAmount > 0">Your promotional discount has been successfully applied.</span>
          <span v-if="!discountCodeValid">The promotional code you entered is not valid.</span>
        </div>
        <button
          @click="getDiscount"
          class="btn btn--secondary input__btn"
          type="button"
        >
          Apply
        </button>
      </div>
      <fieldset class="fieldset mt-2">
        <Lightbox
          v-if="mountLightbox"
          @submit-events="submitPaymentEvents"
          @lightbox-approval="onApproval"
          @lightbox-cancelled="onCancelled"
          @lightbox-error="onError"
          @lightbox-declined="onDeclined"
          :firstName="billingFirstName"
          :lastName="billingLastName"
          :address="billingAddress"
          :zip="billingZip"
          :billingApartmentNumber="billingApartmentNumber"
          :billingCustomerServiceAgreement="billingCustomerServiceAgreement"
          :formCriteriaMet="formCriteriaMet"
        />
      </fieldset>

      <div class="payment-form__wastepay-badge">
        <img class="payment-form__creditcards" :src="wastePayBadge">
      </div>
    </form>
  </div>

  <!-- Payment Processing Modal -->
  <modal :isOpen=paymentIsProcessing :showClose="false">
    <template v-slot:content>
      <img class="theme-logo" :src="themeLogo" />
      <img class="spin" :src="loadingSpinner" />
      <h3 class="legend">Processing your payment</h3>
      <p>This may take a few moments. Please do not click back, close or refresh your browser while we process your information.</p>
    </template>
  </modal>

   <!-- Salesforce Error Modal -->
    <modal class="salesforce-modal-error" :isOpen=showErrorModal :showClose="false">
      <template v-slot:content>
        <div>
          <img class="salesforce-modal-error__img" :src="themeLogo" />
          <h3 class="salesforce-modal-error__heading">We're Sorry, But Something Went Wrong.</h3>
          <p class="mb-1" >Unfortunately, our servers have encountered an error and we were unable to process your order.</p>
          <p class="mb-1" ><strong>Your order information was not saved into our system. Please call our team of dumpster rental experts during business hours to finalize your order.</strong> </p>
          <div><a class="salesforce-modal-error__phone" :href="`tel:+${phoneNumber}`">{{phoneNumber}}</a></div>
        </div>
      </template>
    </modal>
</template>
<script>

import { mapState, mapActions, mapGetters } from 'vuex'
import { addDays, lightFormat } from 'date-fns'
import * as Sentry from '@sentry/vue'
import StepperCard from '@/components/StepperCard.vue'
import Modal from '@/components/Modal.vue'
import Lightbox from '@/components/converge/Lightbox.vue'
import Dismissable from '@/components/Dismissable.vue'
import config from '@/config'

import {
  submitPaymentEvents,
  paymentPageViewEvents,
  getLast4CC
} from '@/utilities/payment'

import { createForNewCustomer } from '@/utilities/paymentApproval'
import { sendConfirmationEmail } from '@/utilities/sendEmail'
import { saveTransaction, flagTransaction } from '@/utilities/saveTransaction'

import {
  getNumericValue,
  setupAutocomplete,
  getFormattedPhoneNumber,
  getAbandonedDNIS,
  getDNIS,
  debrisMap,
  getCCType
} from '@/utilities.js'

const { cdn, companyName, companyNameCSA, phoneNumber, links: { scheduleAPickup, prohibitedItems, serviceAgreementLink } } = config

// TODO:
// Mounted function cleanup/WTF
// Move email call to backend

export default {
  components: {
    StepperCard,
    Modal,
    Lightbox,
    Dismissable
  },
  beforeRouteEnter (to, from, next) {
    if (to.name === 'Payment' && from.name !== 'Service') {
      next('/')
    } else {
      next()
    }
  },
  data () {
    return {
      showAddressError: false,
      mountLightbox: true,
      brand: process.env.VUE_APP_BRAND,
      creditCards: `${cdn}credit-cards-logos.svg`,
      wastePayBadge: `${cdn}wastepay-secure-payments-badge.svg`,
      tooltip: `${cdn}tooltip.svg`,
      iconPaymentLock: `${cdn}icons-payment-lock.svg`,
      activeCheckbox: `${cdn}icons-active-checkbox.svg`,
      loadingSpinner: `${cdn}icons-spinner-loading.svg`,
      themeLogo: `${cdn}theme-logo.png`,
      successIcon: `${cdn}success-icon.svg`,
      declinedIcon: `${cdn}declined-icon.svg`,
      resubmitIcon: `${cdn}resubmit-icon.svg`,
      convergeEmbeddedPayment: null,
      paymentIsProcessing: false,
      dismissableErrorMessage: '',
      companyName,
      companyNameCSA,
      phoneNumber,
      scheduleAPickup,
      serviceAgreementLink,
      prohibitedItems,
      showErrorModal: false,
      dateMask: '##/##',
      phoneMask: ['(###) ###-####', '# (###) ###-####'],
      creditCardMask: '#### #### #### ####',
      transactionId: null,
      createForNewCustomerTxnData: {},
      convergeResponse: {},
      last4CC: '',
      timeout: null,
      googleReCaptchaScore: null,
      googleReCaptchaFraudScore: null,
      googleReCaptchaReasons: null
    }
  },
  watch: {
    billingSameAsDelivery () {
      this.resetLightbox()
    },
    consentToReceiveTexts (newValue) {
      if (newValue === false) this.resetLightbox()
    },
    billingCustomerServiceAgreement (newValue) {
      if (newValue === false) this.resetLightbox()
    }
  },
  mounted () {
    const DNIS = getAbandonedDNIS(this.$store.state.customer.utmSource)
    this.$store.commit('customer/DNIS', DNIS)
    const UUID = this.ppcLeadUUID
    const abandonedPayloadAdditions = {
      abandonedCartFlag: true,
      DNIS
    }

    // if this is not from a PPC landing page, an abandoned lead has not been sent yet, the session timeout modal is not open and not on the confirmation page, create an abandoned lead if the customer attempts to exit the experience
    window.addEventListener('beforeunload', (event) => {
      if (!UUID && !this.abandonedLeadSent && !document.querySelector('.session-timeout-modal') && this.$route.path !== '/cart/confirmation') {
        this.createLead(abandonedPayloadAdditions)
        event.preventDefault()
        event.returnValue = ''
      }
    })

    // TODO: This timeout needs cancelled if user converts
    // if this is a PPC lead and the user has not converted in 30 mins, update the PPC lead to abandoned OR create a non PPC abandoned lead
    this.timeout = setTimeout(() => {
      if (UUID) {
        this.updateLead(abandonedPayloadAdditions)
      } else if (!this.abandonedLeadSent) { // we only want to create an abandoned lead if a lead hasn't already been created
        this.createLead(abandonedPayloadAdditions)
      }
    }, 1800000) // 30 mins

    // TODO: Clean this up
    const phoneInput = this.$refs.billingPhone
    const servicePhoneNumber = this.$refs.servicePhoneNumber
    phoneInput.addEventListener('keyup', (e) => {
      const firstChar = parseInt(e.target.value.replace(/[^\d.-]+/g, '').charAt(0))
      const mask = firstChar === 1 ? '1 (###) ###-####' : '(###) ###-####'
      e.target.setAttribute('data-mask', mask)
    })
    servicePhoneNumber.addEventListener('keyup', (e) => {
      const firstChar = parseInt(e.target.value.replace(/[^\d.-]+/g, '').charAt(0))
      const mask = firstChar === 1 ? '1 (###) ###-####' : '(###) ###-####'
      e.target.setAttribute('data-mask', mask)
    })

    paymentPageViewEvents()
    this.initGoogleAutocomplete()

    // Autofill billing phone from customer phone
    if (this.$store.state.customer.phoneNumber) {
      this.$store.commit('customer/billingMobilePhoneNumber', this.$store.state.customer.phoneNumber)
    }
  },
  methods: {
    ...mapActions('cart', [
      'createLead',
      'getDiscount',
      'updateLead'
    ]),
    csaToggle (e) {
      // Accepted terms
      if (e.target.checked) {
        this.$store.state.customer.acceptedCSATimestamp = new Date().toISOString()
        return
      }
      // Rejected terms
      this.$store.state.customer.acceptedCSATimestamp = null
    },
    resetLightbox () {
      // Re-init Lightbox
      this.mountLightbox = false
      this.$nextTick(() => {
        this.mountLightbox = true
      })
    },
    addressInput () {
      this.updateIsBillingAddressValid(false)
      this.resetLightbox()
    },
    addressBlur () {
      // The timeout gives google autocomplete a chance to fire and set isAddressValid
      // This prevents a glimpse of the invalid error message when address is input and selected from the dropdown
      setTimeout(() => {
        if (!this.isBillingAddressValid) {
          this.showAddressError = true
        }
      }, 200)
    },
    // onCancelled is triggered by lightbox after 15 minutes
    onCancelled (data) {
      // Remounting the component will re-initialize lightbox with fresh tokens
      this.mountLightbox = false
      this.$nextTick(() => {
        this.mountLightbox = true
      })
    },
    async onApproval (data) {
      this.paymentIsProcessing = true
      clearTimeout(this.timeout)
      this.convergeResponse = data.convergeResponse
      try {
        let reCaptchaToken
        try {
          reCaptchaToken = await this.generateReCaptchaToken()
        } catch (e) {
          // We do not care about reCAPTCHA right now, we are exploring its reliability in determining bots/fraud.
          // If we ever do want to rely on reCAPTCHA scores then this error should probably stop the transaction.
          console.error(e)
        }

        const createForNewCustomerResult = await createForNewCustomer({
          convergeResponse: this.convergeResponse,
          authToken: data.authToken,
          salesData: {
            amount: this.totalAmount,
            firstName: this.billingFirstName,
            lastName: this.billingLastName,
            address: this.billingStreet || `${this.$store.state.customer.serviceAddress.streetAddress} ${this.$store.state.customer.serviceAddress.streetName}` || '',
            apartmentNumber: this.billingApartmentNumber,
            city: this.billingCity,
            state: this.billingState,
            zip: this.billingZip,
            country: this.billingCountry,
            email: this.billingEmailAddress
          },
          shippingAddress: {
            address: this.serviceAddress.streetAddress,
            city: this.serviceAddress.city,
            state: this.serviceAddress.state,
            zip: this.serviceAddress.zip,
            country: this.serviceAddress.country
          },
          reCaptchaToken,
          phoneNumber: this.billingPhone
        })

        // Optional chaining not available in this project... apologies for the below.
        const reCaptchaAssessment = createForNewCustomerResult.data.reCaptchaAssessment
        const riskAnalysis = reCaptchaAssessment && reCaptchaAssessment.riskAnalysis
        const fraudPreventionAssessment = reCaptchaAssessment && reCaptchaAssessment.fraudPreventionAssessment
        this.googleReCaptchaScore = riskAnalysis && riskAnalysis.score
        this.googleReCaptchaFraudScore = fraudPreventionAssessment && fraudPreventionAssessment.transactionRisk
        this.googleReCaptchaReasons = riskAnalysis && riskAnalysis.reasons

        this.createForNewCustomerTxnData = createForNewCustomerResult.data.data.txn
        this.last4CC = getLast4CC(this.createForNewCustomerTxnData.ssl_card_number)
        const DNIS = getDNIS(this.$store.state.customer.utmSource)
        const deliveryDate = `${this.deliveryDate.getMonth() + 1}/${this.deliveryDate.getDate()}/${this.deliveryDate.getFullYear()}`

        // this fixes an issue with people checking out after 9pm, because of timezones by setting the hours to 0, the date doesn't change to the next day
        this.$store.commit('customer/deliveryDate', new Date(this.deliveryDate.setHours(0, 0, 0, 0)))
        this.$store.commit('customer/billingExpirationDate', this.convergeResponse.ssl_exp_date)
        this.$store.commit('customer/deliveryDateMilliseconds', new Date(deliveryDate).getTime())
        this.$store.commit('customer/DNIS', DNIS)

        // Save Transaction to DB
        this.saveTransaction()
      } catch (e) {
        Sentry.withScope(scope => {
          scope.setTag('netsuite_pay', 'create_for_new_customer')
          scope.setLevel('fatal')
          Sentry.captureException(e)
        })
        this.paymentIsProcessing = false
        this.showErrorModal = true
      }
    },
    onError () {
      console.log('onError')
      this.showErrorModal = true
      this.paymentIsProcessing = false
    },
    // Fired when user selects cancel after declined card
    onDeclined (response) {
      // Remounting the component will re-initialize lightbox with fresh tokens
      this.mountLightbox = false
      this.$nextTick(() => {
        this.mountLightbox = true
      })
    },
    initGoogleAutocomplete () {
      const autocompleteInput = document.getElementById('autocomplete')
      setupAutocomplete(autocompleteInput, (address) => {
        if (address) {
          this.showAddressError = false
          this.updateIsBillingAddressValid(true)
          this.billingAddress = `${address.streetAddress} ${address.streetName}, ${address.city}, ${address.state} ${address.postalCode}`
          this.billingStreet = `${address.streetAddress} ${address.streetName}`
          this.billingCity = address.city
          this.billingState = address.state
          this.billingZip = address.postalCode
          this.billingCountry = address.country
          return
        }
        this.showAddressError = true
        this.updateIsBillingAddressValid(false)
      })
    },
    async sendConfirmationEmail () {
      const selectedDebrisType = this.selectedDebrisType
      const debrisType1 = debrisMap[selectedDebrisType[0]] || ''
      const debrisType2 = debrisMap[selectedDebrisType[1]] || ''
      const rentalPeriod = getNumericValue(this.rentalPeriod)
      const deliveryDate = `${this.deliveryDate.getMonth() + 1}/${this.deliveryDate.getDate()}/${this.deliveryDate.getFullYear()}`
      const serviceStreet = this.serviceApartmentNumber ? `${this.serviceAddress.streetName}, ${this.serviceApartmentNumber}` : this.serviceAddress.streetName
      const serviceCity = this.serviceAddress.city || ''
      const serviceState = this.serviceAddress.state || ''
      const serviceZip = this.serviceAddress.postalCode || ''

      try {
        await sendConfirmationEmail({
          sendTo: this.billingEmailAddress,
          sendToName: `${this.billingFirstName} ${this.billingLastName}`,
          subject: 'Order Confirmation',
          templateObj: `{
            mergeData:[
              {name:"streetAddress",content:"${this.serviceAddress.streetAddress}"},
              {name:"streetName",content:"${serviceStreet}"},
              {name:"city",content:"${serviceCity}"},
              {name:"state",content:"${serviceState}"},
              {name:"serviceZip",content:"${serviceZip}"},
              {name:"deliveryDate",content:"${deliveryDate}"},
              {name:"size",content:"${this.dumpsterSize}"},
              {name:"debrisType1",content:"${debrisType1}"},
              {name:"debrisType2",content:"${debrisType2}"},
              {name:"price",content:"${this.totalAmount}"},
              {name:"tonnage",content:"${this.tonnage}"},
              {name:"rentalPeriod",content:"${rentalPeriod}"},
              {name:"rentalPeriodOverage",content:"${this.rentalPeriodOverage}"},
              {name:"tonnageOverage",content:"${this.tonnageOverage}"},
              {name:"taxAmount",content:"${this.taxAmount}"}
            ]
          }`,
          template: this.brand === 'BD' ? 'ECOM_BD' : 'ECOM_DC',
          brand: this.brand
        })
        this.paymentIsProcessing = false
        this.$router.push('/cart/confirmation')
      } catch (e) {
        console.log('sendConfirmationEmail Error')
        console.log(e)
        // Even if the email fails send to confrimation
        this.paymentIsProcessing = false
        this.$router.push('/cart/confirmation')
      }
    },
    // Update and existing lead (PPC) or create a new lead
    async salesforceHandler () {
      const completedPayloadAdditions = {
        abandonedCartFlag: false,
        ccType: getCCType(this.createForNewCustomerTxnData.ssl_card_short_description),
        DNIS: this.$store.state.customer.DNIS,
        googleReCaptchaFraudScore: this.googleReCaptchaFraudScore,
        googleReCaptchaReasons: Array.isArray(this.googleReCaptchaReasons)
          ? this.googleReCaptchaReasons.join(',')
          : this.googleReCaptchaReasons,
        googleReCaptchaScore: this.googleReCaptchaScore,
        payment: {
          sslApprovalCode: this.createForNewCustomerTxnData.ssl_approval_code,
          sslAvsResponse: this.createForNewCustomerTxnData.ssl_avs_response,
          last4CC: this.last4CC,
          sslToken: this.convergeResponse.ssl_token,
          sslTxnId: this.createForNewCustomerTxnData.ssl_txn_id
        }
      }

      // Update Lead
      if (this.ppcLeadUUID) {
        this.updateLead(completedPayloadAdditions)
          .then(res => { this.sendConfirmationEmail() })
          .catch(error => {
            console.log('Update Salesforce Lead Error', error)
            // Writing to SF Failed, mark as sf_failed in DB
            this.flagTransaction()
          })
        return
      }

      // Create New Lead
      this.createLead(completedPayloadAdditions)
        .then(res => {
          // I'm really not sure why this store commit is needed but the previous dev left the message below
          // we set this to true because if a real conversion SF lead is created, there's no need for an abandoned one
          this.$store.commit('cart/abandonedLeadSent', true)
          this.sendConfirmationEmail()
        })
        .catch(error => {
          console.log('create Salesforce Lead Error', error)
          // Writing to SF Failed, mark as sf_failed in DB
          this.flagTransaction()
        })
    },
    async saveTransaction () {
      try {
        const saveTransactionResponse = await saveTransaction(
          {
            sslResultMessage: this.createForNewCustomerTxnData.ssl_result_message,
            sslApprovalCode: this.createForNewCustomerTxnData.ssl_approval_code,
            sslAvsResponse: this.createForNewCustomerTxnData.ssl_avs_response,
            sslTxnId: this.createForNewCustomerTxnData.ssl_txn_id,
            ccType: getCCType(this.createForNewCustomerTxnData.ssl_card_short_description),
            taxAmount: this.taxAmount,
            amount: this.totalAmount,
            payment: {
              sslApprovalCode: this.createForNewCustomerTxnData.ssl_approval_code,
              sslAvsResponse: this.createForNewCustomerTxnData.ssl_avs_response,
              last4CC: this.last4CC,
              sslToken: this.convergeResponse.ssl_token,
              sslTxnId: this.createForNewCustomerTxnData.ssl_txn_id
            },
            cart: this.$store.state.cart,
            customer: this.$store.state.customer,
            project: this.$store.state.project,
            onPoint: this.$store.state.onPoint,
            dumpster: this.$store.state.dumpster,
            savedCartUtmSource: this.$store.state.cart.savedCartUtmSource,
            savedCartUtmMedium: this.$store.state.cart.savedCartUtmMedium,
            savedCartUtmCampaign: this.$store.state.cart.savedCartUtmCampaign,
            savedCartCreatedAt: this.$store.state.cart.savedCartCreatedAt,
            savedCartUUID: this.$store.state.cart.savedCartUUID
          }
        )
        this.transactionId = saveTransactionResponse.data.uuid
        // Write to SF
        this.salesforceHandler()
      } catch (e) {
        console.log('saveTransaction error')
        console.log(e)
        // Even if the saving to the DB fails still write to SF
        this.salesforceHandler()
      }
    },
    async flagTransaction () {
      try {
        await flagTransaction(this.transactionId)
        this.showErrorModal = true
        this.paymentIsProcessing = false
      } catch (e) {
        console.log('flagTransaction error')
        this.showErrorModal = true
        this.paymentIsProcessing = false
      }
    },
    submitPaymentEvents () {
      submitPaymentEvents()
      return null
    },
    updateIsBillingAddressValid (bool) {
      this.$store.commit('customer/isBillingAddressValid', bool)
    },
    generateReCaptchaToken () {
      return new Promise((resolve, reject) => {
        if (window.grecaptcha == null) {
          return reject(new Error('reCAPTCHA not loaded'))
        }

        if (process.env.VUE_APP_RECAPTCHA_KEY == null || process.env.VUE_APP_RECAPTCHA_KEY === '') {
          return reject(new Error('missing reCAPTCHA_KEY'))
        }

        // Insurance to make sure we don't wait for reCAPTCHA forever. 10 seconds was an arbitrary number picked.
        // The reCAPTCHA script should have been loaded on page load so at this point in the process if it's not ready
        // then it likely never will be.
        const timeoutId = setTimeout(() => {
          reject(new Error('reCAPTCHA took too long to be in a ready state'))
        }, 10000)

        window.grecaptcha.enterprise.ready(async () => {
          const token = await window.grecaptcha
            .enterprise
            .execute(process.env.VUE_APP_RECAPTCHA_KEY, { action: 'TRANSACTION' })
          clearTimeout(timeoutId)
          resolve(token)
        })
      })
    },
    updateBillingFields (event) {
      // This method will reset the custom validity of all billing fields when "billing same as service" is checked.
      // Without this, if any were previously marked invalid they would be stuck that way after the fieldset is hidden, preventing checkout.
      if (event.target.checked) {
        const billingFieldset = this.$refs.billingFields
        Array.from(billingFieldset.elements).forEach(input => input.setCustomValidity(''))
      }
    }
  },
  computed: {
    ...mapState('cart', [
      'abandonedLeadSent',
      'discountAmount',
      'discountCodeValid',
      'item'
    ]),
    ...mapState('customer', [
      'isServiceAddressValid',
      'isBillingAddressValid'
    ]),
    ...mapGetters('cart', ['totalAmount']),
    formCriteriaMet () {
      return this.billingZip &&
        this.billingEmailAddress &&
        this.billingPhone &&
        this.billingAddress &&
        this.billingCustomerServiceAgreement &&
        this.billingFirstName &&
        this.billingLastName &&
        !this.showErrorModal &&
        this.isBillingAddressValid
    },
    billingFirstName: {
      get () {
        return this.$store.state.customer.billing.billingFirstName
      },
      set (value) {
        this.$store.commit('customer/billingFirstName', value)
      }
    },
    billingLastName: {
      get () {
        return this.$store.state.customer.billing.billingLastName
      },
      set (value) {
        this.$store.commit('customer/billingLastName', value)
      }
    },
    billingEmailAddress: {
      get () {
        return this.$store.state.customer.billing.billingEmailAddress
      },
      set (value) {
        this.$store.commit('customer/billingEmailAddress', value)
      }
    },
    billingPhone: {
      get () {
        return this.$store.state.customer.billing.billingPhone
      },
      set (value) {
        this.$store.commit('customer/billingPhone', getFormattedPhoneNumber(value))
      }
    },
    billingApartmentNumber: {
      get () {
        return this.$store.state.customer.billing.billingAddress.apartmentNumber
      },
      set (value) {
        this.$store.commit('customer/billingApartmentNumber', value)
      }
    },
    serviceApartmentNumber: {
      get () {
        return this.$store.state.customer.serviceAddress.apartmentNumber
      }
    },
    billingAddress: {
      get () {
        return this.$store.state.customer.billing.billingAddress.address
      },
      set (value) {
        this.$store.commit('customer/billingAddress', value)
      }
    },
    billingStreet: {
      get () {
        return this.$store.state.customer.billing.billingAddress.street
      },
      set (value) {
        this.$store.commit('customer/billingStreet', value)
      }
    },
    billingCity: {
      get () {
        return this.$store.state.customer.billing.billingAddress.city
      },
      set (value) {
        this.$store.commit('customer/billingCity', value)
      }
    },
    billingState: {
      get () {
        return this.$store.state.customer.billing.billingAddress.state
      },
      set (value) {
        this.$store.commit('customer/billingState', value)
      }
    },
    billingZip: {
      get () {
        return this.$store.state.customer.billing.billingAddress.zip
      },
      set (value) {
        this.$store.commit('customer/billingZip', value)
      }
    },
    billingCountry: {
      get () {
        return this.$store.state.customer.billing.billingAddress.country
      },
      set (value) {
        this.$store.commit('customer/billingCountry', value)
      }
    },
    billingCustomerServiceAgreement: {
      get () {
        return this.$store.state.customer.billingCustomerServiceAgreement
      },
      set (value) {
        this.$store.commit('customer/billingCustomerServiceAgreement', value)
      }
    },
    billingSameAsDelivery: {
      get () {
        return this.$store.state.customer.billingSameAsDelivery
      },
      set (value) {
        this.$store.commit('customer/billingSameAsDelivery', value)
      }
    },
    billingMobilePhoneNumber: {
      get () {
        return this.$store.state.customer.billing.billingMobilePhoneNumber
      },
      set (value) {
        this.$store.commit('customer/billingMobilePhoneNumber', getFormattedPhoneNumber(value))
      }
    },
    consentToReceiveTexts: {
      get () {
        return this.$store.state.customer.consentToReceiveTexts
      },
      set (value) {
        this.$store.commit('customer/consentToReceiveTexts', value)
      }
    },
    discountCode: {
      get () {
        return this.$store.state.cart.discountCode
      },
      set (value) {
        this.$store.commit('cart/discountCode', value)
      }
    },
    serviceAddress () {
      return this.$store.state.customer.serviceAddress
    },
    deliveryDate () {
      return this.$store.state.customer.deliveryDate
    },
    endOfRentalPeriodDate () {
      const intRentalPeriod = parseInt(this.rentalPeriod)
      const endDate = addDays(this.deliveryDate, intRentalPeriod)
      return lightFormat(endDate, 'MM-dd-yyyy')
    },
    dumpsterSize () {
      return this.$store.state.cart.item.size
    },
    selectedDebrisType () {
      return this.$store.state.project.selectedDebrisType
    },
    tonnage () {
      return this.$store.state.cart.item.tonnage
    },
    tonnageOverage () {
      return this.$store.state.cart.item.tonnageOverage
    },
    rentalPeriod () {
      return getNumericValue(this.$store.state.cart.item.rentalPeriod)
    },
    rentalPeriodOverage () {
      return this.$store.state.cart.item.rentalPeriodOverage
    },
    ppcLeadUUID () {
      return this.$store.state.cart.ppcLeadUUID
    }
  }
}
</script>
